import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { DownOutlined, UserOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { bottomNavStyle } from '../../../layouts/LoggedLayout.styles';
import { useProject } from '../hooks';
import { getPhaseStatusColor, getPhaseStatusText } from '../utils/phaseFactory';
import { ProjectPhaseSheet } from './ProjectPhaseSheet';

const { useToken } = theme;

export const ProjectMobileBottomNav: React.FC = () => {
  const { token } = useToken();
  const navigate = useNavigate();
  const { project, phases, activePhase, loading } = useProject();
  const [sheetOpen, setSheetOpen] = useState(false);

  const activePhaseIndex = useMemo(() => {
    const idx = phases.findIndex(p => p.id === activePhase?.id);
    return idx === -1 ? 0 : idx;
  }, [phases, activePhase?.id]);

  if (!project || loading) return null;

  return (
    <>
      <div
        style={{
          ...bottomNavStyle,
          justifyContent: 'space-between',
          padding: '0 16px',
        }}
        role="navigation"
        aria-label="Project navigation">
        {/* Left section: status + numbered phase */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => setSheetOpen(true)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              setSheetOpen(true);
            }
          }}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            cursor: 'pointer',
            overflow: 'hidden',
            gap: '2px',
          }}>
          <div
            style={{
              fontSize: '11px',
              fontWeight: 'normal',
              color: token.colorTextSecondary,
              lineHeight: '1.2',
            }}>
            {activePhase ? getPhaseStatusText(activePhase) : ''}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
            {/* Phase number circle */}
            <div
              style={{
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                backgroundColor: activePhase
                  ? getPhaseStatusColor(activePhase, token)
                  : token.colorTextDisabled,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 'bold',
                flexShrink: 0,
              }}>
              {activePhaseIndex + 1}
            </div>
            {/* Phase name + dropdown arrow */}
            <div
              style={{
                fontSize: '14px',
                fontWeight: 'bold',
                color: token.colorText,
                lineHeight: '1.3',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                overflow: 'hidden',
              }}>
              <span
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {activePhase?.name || 'Select Phase'}
              </span>
              <DownOutlined style={{ fontSize: '10px', flexShrink: 0 }} />
            </div>
          </div>
        </div>

        {/* Divider */}
        <div
          style={{
            width: '1px',
            height: '32px',
            backgroundColor: token.colorSplit,
            margin: '0 12px',
            flexShrink: 0,
          }}
        />

        {/* Right section: profile icon */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate('/user/profile')}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              navigate('/user/profile');
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px',
            cursor: 'pointer',
            flexShrink: 0,
          }}>
          <UserOutlined style={{ fontSize: '22px', color: '#000000' }} />
        </div>
      </div>

      <ProjectPhaseSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
};
